import React from 'react';
import { useParams } from "react-router-dom";
import { Card } from "baseui/card";
import { Tabs, Tab } from 'baseui/tabs';
import { Block } from 'baseui/block';
import { Button } from "baseui/button";
import { StyledLink } from 'baseui/link';
import { UserPlusIcon } from "@/assets/icons/user-plus";
import { useNavigate } from "react-router-dom";
import useJobStore from "../../store/jobs";
import { JobCard } from "../../components/jobs/job";
import { NavigateBack } from "../../components/navigation/navigate-back";
import { VetsmithText } from '../../components/VetsmithText';
import DataTable, { FilterTypes } from '@/components/data-table/data-table';
import { CandidateData } from '@/network/definitions/job';
import { getApplicationReviewTag, getReviewerComments } from './candidate-list';

interface AddCandidateButtonProps {
    jobId: number;
    disabled?: boolean;
}

const allCandidatesColumnsConfig = [
    { field: 'email', headerName: 'Email', sortable: true, filterType: FilterTypes.Text, pinned: true },
    { field: 'name', headerName: 'Name', filterType: FilterTypes.Text },
    {
        field: 'job_title',
        headerName: 'Job role',
        render: (row: CandidateData) => `${row.job_id} - ${row.job_title}`
    },
    {
        field: 'application_review_status',
        headerName: 'Review status',
        render: (row: CandidateData) => getApplicationReviewTag(row.application_review_status)
    },
    {
        field: 'rejection_reason',
        headerName: 'Rejection reason',
        render: (row: CandidateData) => getReviewerComments(row.reviewer_comments),
    },
    {
        field: 'job_request_link',
        headerName: 'Interest Check Form',
        render: (row: CandidateData) => row.job_request_status,
    },
    {
        field: 'vetsmith',
        headerName: 'Automated or pre-screening Test',
        render: (row: CandidateData) => <VetsmithText item={row} />,
    },
    {
        field: 'mti_status',
        headerName: 'First interview round status',
    },
    {
        field: 'onboarding_status',
        headerName: 'Onboarding status',
    },
    { field: 'uploaded_date', headerName: 'Uploaded On' },
];

const selectedCandiatesColumnConfig = [
    { field: 'email', headerName: 'Email', sortable: true, filterType: FilterTypes.Text, pinned: true },
    { field: 'name', headerName: 'Name', filterType: FilterTypes.Text },
    {
        field: 'onboarding_status',
        headerName: 'Onboarding status',
    },
    {
        field: 'trial_start_date',
        headerName: 'Trial start date',
    },
    {
        field: 'final_developer_rate',
        headerName: 'Final developer rate',
    },
    { field: 'uploaded_date', headerName: 'Uploaded On' },
];

const AddCandidateButton = ({
    jobId,
    disabled = false
}: AddCandidateButtonProps) => {
    const navigate = useNavigate();
    return (
        <Button
            onClick={() => navigate(`/job/${jobId}/add-candidate`)}
            overrides={{
                BaseButton: {
                    style: ({ $theme }) => ({
                        paddingLeft: $theme.sizing.scale600,
                        paddingRight: $theme.sizing.scale600,
                        borderRadius: $theme.borders.radius400, // Rounded corners
                    }),
                },
            }}
            startEnhancer={() => <UserPlusIcon />} // Icon on the left
            disabled={disabled}
        >
            Add a candidate
        </Button>
    );
};

const JobDetailsTabs = ({ job }) => {
    const [activeKey, setActiveKey] = React.useState('1');

    return (
        <Card
            overrides={{
                Root: {
                    style: ({ $theme }) => ({
                        backgroundColor: $theme.colors.backgroundSecondary, // Customize the color
                        padding: $theme.sizing.scale100,
                        minWidth: "100%",
                        width: "100%",
                    }),
                },
            }}
        >
            <Tabs
                activeKey={activeKey}
                onChange={({ activeKey }) => {
                    // @ts-ignore
                    setActiveKey(activeKey);
                }}
                overrides={{
                    TabBar: {
                        style: ({ $theme }) => ({
                            borderBottom: `2px solid ${$theme.colors.backgroundPrimary}`,
                            backgroundColor: $theme.colors.backgroundSecondary
                        })
                    }
                }}
            >
                <Tab title="Job details">
                    <Block
                        padding="scale100"
                    >
                        {job.agenciesJobDescriptionLink ? (
                            <StyledLink
                                href={job.agenciesJobDescriptionLink}
                                target="_blank"
                                $style={({ $theme }) => ({
                                    fontSize: $theme.typography.LabelLarge.fontSize,
                                    color: $theme.colors.accent,
                                    fontWeight: 'bold',
                                })}
                            >
                                Job Description Link
                            </StyledLink>
                        ) : 'Job description link is not provided'}
                    </Block>
                </Tab>
                <Tab title="All candidates">
                    <DataTable<CandidateData>
                        apiEndpoint={`agency-api/candidates`}
                        initialColumnsConfig={allCandidatesColumnsConfig}
                        defaultFilters={{
                            job_id: job.id,
                        }}
                    />
                </Tab>
                <Tab title="Selected candidates">
                    <DataTable<CandidateData>
                        apiEndpoint={`agency-api/candidates`}
                        initialColumnsConfig={selectedCandiatesColumnConfig}
                        defaultFilters={{
                            job_id: job.id,
                            filter: 'approved'
                        }}
                    />
                </Tab>
            </Tabs>
        </Card>
    );
};





const JobDetails = () => {
    const { id = null } = useParams();
    if (!id) return null;
    const getJobById = useJobStore(state => state.getJobById);
    const job = getJobById(Number(id));
    if (!job) return;
    return (
        <>
            <NavigateBack
                route={`/jobs`}
                text="Back to job board"
            />
            <JobCard
                job={job}
                actionComponent={<AddCandidateButton jobId={job.id} disabled={job.active === false} />}
                onCardClick={() => { }}
            />
            <JobDetailsTabs job={job} />
        </>
    )
}

export default JobDetails;